/*
  Inline image carousel for product cards (snippets/card-product.liquid).

  Swiping is native: the track is a CSS scroll-snap container, so the browser moves the images on
  its scrolling thread (they follow the finger, keep momentum and snap), tells a tap from a swipe
  (a scroll never fires the slide link's click) and hands vertical pans to the page.
  This script only mirrors the scroll position into dots / arrows / aria, drives the arrows and
  lazily hydrates images. Listeners live on document once, so cards re-rendered by facets.js or
  the theme editor need no re-binding.
*/
(function () {
  'use strict';

  var PRELOAD_ROOT_MARGIN = '200px 0px';
  var TARGET_TIMEOUT_MS = 1000;

  // Slide index for a scroll offset. Rounds to the nearest slide and never leaves [0, count - 1].
  function indexFromScroll(scrollLeft, slideWidth, count) {
    if (!(slideWidth > 0) || count < 1) return 0;
    var index = Math.round(scrollLeft / slideWidth);
    return Math.max(0, Math.min(count - 1, index));
  }

  // The slides to have loaded while the track sits at scrollLeft: the ones in view plus the next.
  function slidesToHydrate(scrollLeft, slideWidth, count) {
    if (!(slideWidth > 0) || count < 1) return [0];
    var first = Math.max(0, Math.floor(scrollLeft / slideWidth));
    var last = Math.min(count - 1, Math.ceil(scrollLeft / slideWidth) + 1);
    var list = [];
    for (var i = first; i <= last; i++) list.push(i);
    return list;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { indexFromScroll: indexFromScroll, slidesToHydrate: slidesToHydrate };
  }

  if (typeof window === 'undefined' || !window.customElements || customElements.get('card-image-carousel')) return;

  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  var preloadObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              preloadObserver.unobserve(entry.target);
              entry.target.hydrate(entry.target.index + 1);
            });
          },
          { rootMargin: PRELOAD_ROOT_MARGIN }
        )
      : null;

  class CardImageCarousel extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.card-wrapper');
      this.track = this.wrapper && this.wrapper.querySelector('[data-card-carousel-track]');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.track.children);
      this.count = this.slides.length;
      if (this.count < 2) return;

      this.index = 0;
      this.targetIndex = null;
      this.prevButton = this.querySelector('[data-card-carousel-prev]');
      this.nextButton = this.querySelector('[data-card-carousel-next]');
      this.dots = this.querySelectorAll('.card-image-carousel__dot');
      this.status = this.querySelector('[data-card-carousel-status]');
      this.track.carousel = this;

      this.classList.add('is-ready');
      this.wrapper.classList.add('card-wrapper--image-carousel');
      this.render(false);

      if (preloadObserver) preloadObserver.observe(this);
      else this.hydrate(1);
    }

    disconnectedCallback() {
      clearTimeout(this.targetTimer);
      if (preloadObserver) preloadObserver.unobserve(this);
    }

    hydrate(slideIndex) {
      var slide = this.slides && this.slides[slideIndex];
      var img = slide && slide.querySelector('img[data-srcset]');
      if (!img) return;
      // Hydration is already the deferred step; native lazy loading would wait until the slide is
      // scrolled into view and show an empty frame mid-swipe.
      img.loading = 'eager';
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    }

    // Called (at most once per frame) while the track scrolls.
    syncFromScroll() {
      var width = this.track.clientWidth;
      var left = this.track.scrollLeft;
      var self = this;
      slidesToHydrate(left, width, this.count).forEach(function (i) {
        self.hydrate(i);
      });
      var next = indexFromScroll(left, width, this.count);
      // During an arrow-driven smooth scroll the intermediate positions are not a new position;
      // keep showing the destination until the track arrives (or settles elsewhere).
      if (this.targetIndex !== null) {
        if (next !== this.targetIndex) return;
        this.clearTarget();
      }
      if (next === this.index) return;
      this.index = next;
      this.render(false);
    }

    clearTarget() {
      this.targetIndex = null;
      clearTimeout(this.targetTimer);
    }

    // Where the arrows count from: the destination of a scroll still in progress, else the current slide.
    currentIndex() {
      return this.targetIndex !== null ? this.targetIndex : this.index;
    }

    goTo(targetIndex, announce) {
      var next = Math.max(0, Math.min(this.count - 1, targetIndex));
      this.hydrate(next);
      this.hydrate(next + 1);
      var changed = next !== this.index;
      this.index = next;
      this.clearTarget();
      this.targetIndex = next;
      // Fallback for browsers without scrollend, or a swipe that interrupts the animation.
      var self = this;
      this.targetTimer = setTimeout(function () {
        self.targetIndex = null;
        self.syncFromScroll();
      }, TARGET_TIMEOUT_MS);
      this.track.scrollTo({
        left: next * this.track.clientWidth,
        behavior: reducedMotion && reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.render(announce && changed);
    }

    render(announce) {
      var index = this.index;

      this.slides.forEach(function (slide, i) {
        if (i === index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      Array.prototype.forEach.call(this.dots, function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });

      var focused = document.activeElement;
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.count - 1;
      // A button that just became disabled drops focus; hand it to the other button.
      if (focused === this.prevButton && this.prevButton.disabled && this.nextButton) this.nextButton.focus();
      if (focused === this.nextButton && this.nextButton.disabled && this.prevButton) this.prevButton.focus();

      if (announce && this.status) {
        this.status.textContent = (this.dataset.statusTemplate || '[index] / [count]')
          .replace('[index]', index + 1)
          .replace('[count]', this.count);
      }
    }
  }

  customElements.define('card-image-carousel', CardImageCarousel);

  // ---- Delegated interaction -------------------------------------------------

  // scroll does not bubble; the capture phase on document still sees every element's scroll.
  var pendingTracks = [];
  var frameRequested = false;

  function flushScroll() {
    frameRequested = false;
    var tracks = pendingTracks;
    pendingTracks = [];
    tracks.forEach(function (track) {
      if (track.carousel && track.isConnected) track.carousel.syncFromScroll();
    });
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || !track.carousel) return;
      if (pendingTracks.indexOf(track) === -1) pendingTracks.push(track);
      if (!frameRequested) {
        frameRequested = true;
        requestAnimationFrame(flushScroll);
      }
    },
    { capture: true, passive: true }
  );

  // Settle on the final position even if the last scroll frame was skipped (where supported).
  document.addEventListener(
    'scrollend',
    function (event) {
      var track = event.target;
      if (!track || !track.carousel || !track.isConnected) return;
      track.carousel.clearTarget();
      track.carousel.syncFromScroll();
    },
    { capture: true, passive: true }
  );

  // Warm the next image as soon as a finger lands, before the swipe reveals it.
  document.addEventListener(
    'pointerdown',
    function (event) {
      var track = event.target.closest && event.target.closest('[data-card-carousel-track]');
      if (track && track.carousel) track.carousel.hydrate(track.carousel.index + 1);
    },
    { passive: true }
  );

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.card-image-carousel__button');
    if (!button) return;
    event.preventDefault();
    var carousel = button.closest('card-image-carousel');
    if (carousel && carousel.classList.contains('is-ready')) {
      carousel.goTo(carousel.currentIndex() + (button.hasAttribute('data-card-carousel-prev') ? -1 : 1), true);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    var button = event.target.closest && event.target.closest('.card-image-carousel__button');
    if (!button) return;
    var carousel = button.closest('card-image-carousel');
    if (!carousel || !carousel.classList.contains('is-ready')) return;
    event.preventDefault();
    carousel.goTo(carousel.currentIndex() + (event.key === 'ArrowLeft' ? -1 : 1), true);
  });

  // error does not bubble; listen in the capture phase. A failed slide stays an empty,
  // fixed-size box instead of showing a broken-image icon.
  document.addEventListener(
    'error',
    function (event) {
      var img = event.target;
      if (img && img.tagName === 'IMG' && img.closest('.card-image-carousel__slide')) img.classList.add('is-broken');
    },
    true
  );
})();
